/**
 * FE#447 — when the extension next offers a message to the server, WITHOUT a clock model of its own.
 *
 * The server alone decides "is it due?" (on the clock that minted the dueDate) and "is it too late?". The
 * extension offers every pending message and obeys the verdict: on `not_yet_due` the server says, on ITS
 * timeline, how long to wait (`retryAfterMs`); on `window_expired` the message is dead and the reaper will
 * terminalize it. Either way the extension just stops re-offering it for a while.
 *
 * Holds are DURATIONS (Date.now() deltas), so a wrong machine clock cannot shorten or lengthen them; a clock
 * stepped backwards simply expires the hold. They are keyed on the fields that define "when": an edit that
 * moves the dueDate (or re-stamps the creator skew) is a different key and is offered at once.
 */
import { CREATOR_SKEW_MAX_MS } from '@/bt-common/scheduling/server-timeline';
import { offerHeldLogText, offerDroppedLogText, pacerSkipLogText, type PacerSkipReason } from '@/bt-common/scheduling/pacer-log-text';
import { overdueWindowMs } from '@/bt-common/services/utilsCommon';
import type { SendProtocolResult } from '@/bt-common/scheduling/send-protocol.types';

/** A server hint is never trusted for longer than this: it is revisited hourly, and a missed update self-heals. */
export const MAX_HOLD_MS = 60 * 60 * 1000;
/** No hint (older server) → still never a hot loop. */
export const FALLBACK_HOLD_MS = 60 * 1000;
/** Slack on top of the widest believable skew, matching the server's near-due tolerance. */
const OFFER_SLACK_MS = 5 * 60 * 1000;

export interface HoldableMessage {
  _id: string;
  dueDate?: string | Date | null;
  creatorClockSkewMs?: number | null;
}

const keyOf = (m: HoldableMessage): string => {
  const due = m.dueDate === undefined || m.dueDate === null ? NaN : new Date(m.dueDate).getTime();
  return `${Number.isNaN(due) ? '' : due}|${m.creatorClockSkewMs ?? 0}`;
};

/** Operator rule: the extension also expires messages itself (no flooding the server with stale ones). */
export const MAX_CONSECUTIVE_DENIES = 20;
/** Local backstop slack: the message's own overdue window (default 30 min) + this, to absorb a wrong machine clock. */
export const LOCAL_EXPIRY_MARGIN_MS = 2 * 60 * 60 * 1000;

export interface OfferHolds {
  hold(message: HoldableMessage, retryAfterMs?: unknown): void;
  /** Terminal: never offer this (dueDate, skew) again — the server said it is expired. An edit releases it. */
  expire(message: HoldableMessage): void;
  /** An UNEXPLAINED deny (not a deferral, backoff, grace wait or rate limit). Returns true once
   *  MAX_CONSECUTIVE_DENIES in a row is reached → expired. */
  noteDeny(message: HoldableMessage): boolean;
  /** Any outcome that is not an unexplained deny (grant, send, backoff, gateway grace…): the streak is broken. */
  resetDenies(id: string): void;
  isHeld(message: HoldableMessage): boolean;
  release(id: string): void;
  size(): number;
}

export function createOfferHolds(nowFn: () => number = Date.now): OfferHolds {
  const holds = new Map<string, { key: string; heldAt: number; holdMs: number }>();
  const denies = new Map<string, { key: string; count: number }>();
  return {
    expire(message) {
      holds.set(message._id, { key: keyOf(message), heldAt: nowFn(), holdMs: Number.POSITIVE_INFINITY });
    },
    noteDeny(message) {
      const key = keyOf(message);
      const prev = denies.get(message._id);
      const count = prev && prev.key === key ? prev.count + 1 : 1;
      denies.set(message._id, { key, count });
      if (count < MAX_CONSECUTIVE_DENIES) return false;
      denies.delete(message._id);
      holds.set(message._id, { key, heldAt: nowFn(), holdMs: Number.POSITIVE_INFINITY });
      return true;
    },
    hold(message, retryAfterMs) {
      const valid = typeof retryAfterMs === 'number' && Number.isFinite(retryAfterMs) && retryAfterMs > 0;
      const holdMs = valid ? Math.min(retryAfterMs, MAX_HOLD_MS) : FALLBACK_HOLD_MS;
      holds.set(message._id, { key: keyOf(message), heldAt: nowFn(), holdMs });
    },
    isHeld(message) {
      const h = holds.get(message._id);
      if (!h) return false;
      const elapsed = nowFn() - h.heldAt;
      if (h.key !== keyOf(message) || elapsed < 0 || elapsed >= h.holdMs) {
        holds.delete(message._id);
        return false;
      }
      return true;
    },
    resetDenies: (id) => { denies.delete(id); },
    release: (id) => { holds.delete(id); denies.delete(id); },
    size: () => holds.size,
  };
}

export const offerHolds: OfferHolds = createOfferHolds();

/**
 * A loose upper bound on what is worth offering at all: a dueDate further ahead than ANY believable clock skew
 * (the server's own 26 h cap) cannot be due yet, so it is not offered until it is nearer. Not a clock model — it
 * never excludes a message the server could grant, and it never ages anything OUT. Missing/invalid dueDate →
 * offerable (fail-open).
 */
export function isOfferablePlausibly(message: { dueDate?: string | Date | null }, localNowMs: number): boolean {
  if (message.dueDate === undefined || message.dueDate === null) return true;
  const due = new Date(message.dueDate).getTime();
  if (Number.isNaN(due)) return true;
  return due <= localNowMs + CREATOR_SKEW_MAX_MS + OFFER_SLACK_MS;
}

/** Server deny reasons after which the server will NEVER grant this message again: drop it at once. */
export const TERMINAL_SLOT_REASONS: ReadonlySet<string> = new Set(['window_expired', 'already_terminal', 'not_found', 'exhausted']);
/** Deny reasons that are expected waits and must never count towards the unexplained-deny cap. */
export const BENIGN_DENY_REASONS: ReadonlySet<string> = new Set(['backoff', 'gateway_preferred', 'rate_limited', 'not_yet_due']);

/** Local backstop on THIS machine's clock: a non-campaign message more than its own overdue window (default
 *  30 min; `overdueWindowMs`) + 2 h past its dueDate is no longer offered. Deliberately generous so a skewed
 *  clock cannot expire a live message; the server's overdue deny is the precise gate. Campaign steps are paced
 *  and have their own server windows. */
export function isPastLocalExpiry(
  message: { dueDate?: string | Date | null; campaignId?: string | null; retryLimitDuration?: number | null; retryLimitUnit?: string | null },
  localNowMs: number,
): boolean {
  if (message.campaignId) return false;
  if (message.dueDate === undefined || message.dueDate === null) return false;
  const due = new Date(message.dueDate).getTime();
  if (Number.isNaN(due)) return false;
  const window = overdueWindowMs({
    retryLimitDuration: message.retryLimitDuration ?? undefined,
    retryLimitUnit: (message.retryLimitUnit ?? undefined) as never,
  });
  return localNowMs > due + window + LOCAL_EXPIRY_MARGIN_MS;
}

/**
 * #409 — after a send that hung until the 180s send guard, this message is not re-offered for this long. The pacer
 * serves the most-retried message first, so without the hold a hanging group send would go straight back to the
 * front and hang again on every tick, starving the rest of its due-minute batch past the overdue window.
 */
export const SEND_HANG_HOLD_MS = 10 * 60 * 1000;
/** The send guard's timeout, and the prepareChat hang older builds still write. */
const SEND_HANG_RE = /Timeout in sendMessageViaWhatsapp|prepareChat:hang/;

/**
 * FE#447: obey the server's verdict about WHEN to come back — never this machine's own clock.
 *  • not_yet_due            → re-offer after the server's `retryAfterMs` (server timeline);
 *  • terminal slot reasons  → the server will never grant it (window_expired, already_terminal,
 *                             not_found, exhausted): dropped from the queue, never re-offered;
 *  • backoff / grace wait / rate limit → expected waits: they break the unexplained-deny streak;
 *  • any other deny         → counts; MAX_CONSECUTIVE_DENIES in a row expires it.
 * Anything that is not a deny (sent, failed, skipped…) also breaks the streak; a send that hung (#409) is held.
 */
export function applySendResultToHolds(holds: OfferHolds, message: HoldableMessage, result: SendProtocolResult): HoldEvent | null {
  if (result.status === 'lock_denied') {
    const slotReason = 'slotReason' in result ? result.slotReason : undefined;
    if (result.reason === 'not_yet_due') {
      holds.resetDenies(message._id);
      holds.hold(message, result.retryAfterMs);
      const hint = result.retryAfterMs;
      const holdMs = typeof hint === 'number' && Number.isFinite(hint) && hint > 0 ? Math.min(hint, MAX_HOLD_MS) : FALLBACK_HOLD_MS;
      return holdMs >= LOGGABLE_HOLD_MS ? { kind: 'hold', holdMs, reason: 'not_yet_due' } : null;
    } else if (slotReason && TERMINAL_SLOT_REASONS.has(slotReason)) {
      holds.expire(message);
      return { kind: 'expire', reason: slotReason };
    } else if (BENIGN_DENY_REASONS.has(result.reason) || (slotReason && BENIGN_DENY_REASONS.has(slotReason))) {
      holds.resetDenies(message._id);
    } else if (holds.noteDeny(message)) {
      return { kind: 'expire', reason: 'deny_cap' };
    }
    return null;
  }
  holds.resetDenies(message._id);
  if (result.status === 'failed' && SEND_HANG_RE.test(result.error)) {
    holds.hold(message, SEND_HANG_HOLD_MS);
    return { kind: 'hold', holdMs: SEND_HANG_HOLD_MS, reason: 'send_hang' };
  }
  return null;
}

/** H16 (#534): a hold at least this long (or any expiry) suppresses a message long enough that ops need a durable row. */
export const LOGGABLE_HOLD_MS = 5 * 60 * 1000;

/** What applySendResultToHolds did that is worth a durable attempt row; null = nothing (short holds stay silent). */
export type HoldEvent =
  | { kind: 'hold'; holdMs: number; reason: string }
  | { kind: 'expire'; reason: string };

/** Customer-free, ops-readable log line for a HoldEvent. */
export function describeHoldEvent(ev: HoldEvent): string {
  return ev.kind === 'hold' ? offerHeldLogText(Math.round(ev.holdMs / 60_000), ev.reason) : offerDroppedLogText(ev.reason);
}

/**
 * H16 (#534) — pacer skips that leave a still-pending message un-offered with no server verdict and no row.
 * `local_expiry`: past the local backstop, never offered again. `implausible_due`: an already-attempted message
 * dated past the plausibility bound (a fresh far-future schedule is the normal resting state and is not reported).
 */
export type { PacerSkipReason };

/** Customer-free, ops-readable log line for a pacer skip. */
export function describeSkip(reason: PacerSkipReason): string {
  return pacerSkipLogText(reason);
}

export type SkippedMessage = { _id: string; retryCount?: number | null };

export type SkipReporter = {
  report(message: SkippedMessage, reason: PacerSkipReason): void;
  /** Drop dedupe keys for ids no longer in the pending list, so the set stays bounded by it. */
  prune(liveIds: ReadonlySet<string>): void;
};

/** The pacer ticks every ~5 s over the full list: fire `sink` once per message+reason, not once per tick. */
export function createSkipReporter(sink: (message: SkippedMessage, reason: PacerSkipReason) => void): SkipReporter {
  const seen = new Map<string, Set<PacerSkipReason>>();
  return {
    report(message, reason) {
      const reasons = seen.get(message._id) ?? new Set<PacerSkipReason>();
      if (reasons.has(reason)) return;
      reasons.add(reason);
      seen.set(message._id, reasons);
      sink(message, reason);
    },
    prune(liveIds) {
      for (const id of seen.keys()) if (!liveIds.has(id)) seen.delete(id);
    },
  };
}
